import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Receipt, ShieldCheck, Zap, FileText, ArrowRight, CircleDollarSign } from "lucide-react";
import { STORE } from "@/lib/config";

const BENEFITS = [
  { icon: Zap, label: "Brza montaža", desc: "Instalacija i puštanje kase u rad" },
  { icon: ShieldCheck, label: "Prijava u SUF", desc: "Kompletna dokumentacija umjesto vas" },
  { icon: FileText, label: "Obuka osoblja", desc: "Naučimo vas kako se koristi u praksi" },
  { icon: CircleDollarSign, label: "Servis i podrška", desc: "Kad zapne — mi smo tu za vas" },
];

export default function Fiscalization() {
  const [key, setKey] = useState(0);

  // Restart animation every 6s
  useEffect(() => {
    const t = setInterval(() => setKey((k) => k + 1), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="fiskalizacija"
      data-testid="fiscalization-section"
      className="relative py-24 lg:py-32 bg-[#FAFAFA] overflow-hidden"
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#D32F2F 1px, transparent 1px), linear-gradient(90deg, #D32F2F 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT — text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D32F2F]/10 text-[#D32F2F] text-xs uppercase tracking-[0.2em] font-bold mb-5">
              <Receipt className="w-3.5 h-3.5" />
              Fiskalizacija kasa
            </div>

            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-neutral-900 leading-[1.05] mb-5"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Fiskalna kasa —
              <span className="block text-[#D32F2F]">bez muke, bez papirologije.</span>
            </h2>

            <p className="text-neutral-600 text-lg leading-relaxed mb-8 max-w-lg">
              Otvarate obrt ili firmu? Trebate zamijeniti staru kasu? U MOBY SHOP-u vam
              nudimo <strong className="text-neutral-900">kompletnu uslugu fiskalizacije</strong> — od
              odabira uređaja, prijave u poreznu upravu, do obuke i redovne tehničke podrške.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`fisk-benefit-${i}`}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white border border-neutral-200 hover:border-[#D32F2F] hover:shadow-md transition-all"
                >
                  <div className="w-9 h-9 grid place-items-center bg-[#D32F2F]/10 text-[#D32F2F] rounded-lg shrink-0">
                    <b.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">{b.label}</div>
                    <div className="text-xs text-neutral-500 leading-tight mt-0.5">{b.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <a
              href="#kontakt"
              data-testid="fisk-cta"
              className="group inline-flex items-center gap-3 bg-neutral-900 hover:bg-[#D32F2F] text-white px-7 py-3.5 rounded-full font-semibold transition-all"
            >
              Zatraži ponudu za fiskalizaciju
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* RIGHT — animated cash register */}
          <div className="relative flex justify-center order-first lg:order-last">
            <CashRegister animKey={key} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ Animated cash register + printing receipt ============ */

function CashRegister({ animKey }) {
  return (
    <div className="relative w-full max-w-md">
      {/* Cash register body */}
      <div className="relative">
        {/* Screen top */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto w-56 h-16 bg-neutral-900 rounded-t-2xl border-4 border-neutral-800 relative overflow-hidden shadow-xl"
        >
          {/* LED display */}
          <div
            className="absolute inset-2 rounded bg-gradient-to-b from-emerald-950 to-emerald-900 grid place-items-center font-mono text-emerald-300 text-lg tracking-widest"
            style={{ fontFamily: "monospace", textShadow: "0 0 8px rgba(52,211,153,0.6)" }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={animKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="cash-display"
              >
                <TypingNumber target={247.5} />
              </motion.span>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Main body */}
        <div className="relative bg-gradient-to-b from-[#B71C1C] via-[#D32F2F] to-[#8b1414] rounded-3xl p-6 pt-8 shadow-2xl shadow-red-900/30 border border-red-950">
          {/* Brand label */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 text-white/80 text-[10px] uppercase tracking-[0.3em] font-bold">
            MOBY · POS
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-4 gap-2.5 mb-4 mt-4">
            {[
              "7", "8", "9", "×",
              "4", "5", "6", "-",
              "1", "2", "3", "+",
              "0", ".", "C", "=",
            ].map((k, i) => (
              <motion.button
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15 + i * 0.02, type: "spring", stiffness: 200 }}
                className={`aspect-square rounded-lg text-sm font-bold shadow-md border transition-transform hover:scale-95 ${
                  ["+", "-", "×", "=", "C"].includes(k)
                    ? "bg-neutral-900 border-black text-white"
                    : "bg-white border-neutral-200 text-neutral-800"
                }`}
              >
                {k}
              </motion.button>
            ))}
          </div>

          {/* Print slot */}
          <div className="relative h-8 bg-neutral-950 rounded-t-lg mx-4 shadow-inner z-20">
            <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-1 bg-black rounded-full" />
          </div>
        </div>

        {/* Base */}
        <div className="h-3 mx-6 bg-neutral-800 rounded-b-2xl shadow-xl relative z-20" />

        {/* PRINTING RECEIPT — comes down from the print slot */}
        <div className="relative mx-auto -mt-4 w-56 h-72 overflow-hidden pointer-events-none">
          <motion.div
            key={animKey}
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full bg-white shadow-2xl text-neutral-800 pt-3 pb-4 px-4"
          >
            {/* Zigzag bottom edge */}
            <div
              className="absolute -bottom-2 left-0 right-0 h-2"
              style={{
                background:
                  "linear-gradient(-45deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%), linear-gradient(45deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%)",
                backgroundSize: "12px 12px",
                backgroundPosition: "0 0",
              }}
            />

            <ReceiptContent animKey={animKey} />
          </motion.div>
        </div>
      </div>

      {/* Floating coins */}
      {[...Array(4)].map((_, i) => (
        <motion.div
          key={`coin-${animKey}-${i}`}
          className="absolute w-6 h-6 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 border-2 border-yellow-600 shadow-lg grid place-items-center text-[10px] font-black text-yellow-900"
          initial={{ y: 100, x: 0, opacity: 0, scale: 0 }}
          animate={{
            y: [-60, -120, -60],
            x: [(i - 2) * 25, (i - 2) * 40, (i - 2) * 30],
            opacity: [0, 1, 0],
            rotate: [0, 360],
            scale: [0, 1, 0.8],
          }}
          transition={{ duration: 2.2, delay: 1 + i * 0.15, ease: "easeOut" }}
          style={{ top: "-10px", left: "50%" }}
        >
          KM
        </motion.div>
      ))}
    </div>
  );
}

function ReceiptContent({ animKey }) {
  const LINES = [
    { name: "Kruh (x2)", price: "3.00 KM", d: 0.9 },
    { name: "Mlijeko", price: "2.50 KM", d: 1.05 },
    { name: "Bilježnica A5", price: "4.50 KM", d: 1.2 },
    { name: "Punjač USB-C", price: "18.00 KM", d: 1.35 },
    { name: "Kava", price: "4.00 KM", d: 1.5 },
  ];

  return (
    <div className="text-center text-[10px] leading-tight font-mono">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-base font-black tracking-[0.15em]"
        style={{ fontFamily: "Cabinet Grotesk" }}
      >
        MOBY SHOP
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="text-[8px] text-neutral-500 uppercase tracking-widest">
        Orašje · {STORE.address}
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-[8px] text-neutral-500 mt-0.5">
        ID: 4200000000000 · PDV
      </motion.div>

      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.7, duration: 0.3 }} className="h-px bg-neutral-300 my-1.5 origin-left" />

      <div className="text-left space-y-0.5">
        {LINES.map((line) => (
          <motion.div
            key={`${animKey}-${line.name}`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: line.d, duration: 0.3 }}
            className="flex justify-between"
          >
            <span>{line.name}</span>
            <span className="font-bold">{line.price}</span>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.7, duration: 0.3 }} className="h-px bg-neutral-300 my-1.5 origin-left" />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.85, type: "spring", stiffness: 200 }}
        className="flex justify-between text-sm font-black text-[#D32F2F]"
      >
        <span>UKUPNO:</span>
        <span>32.00 KM</span>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.1 }} className="text-[8px] text-neutral-400 mt-2 uppercase tracking-widest">
        Hvala vam ♥
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }} className="text-[8px] text-neutral-400 mt-0.5">
        {new Date().toLocaleDateString("hr-HR")} · 14:32
      </motion.div>
    </div>
  );
}

function TypingNumber({ target }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let n = 0;
    const step = target / 22;
    const t = setInterval(() => {
      n = Math.min(n + step, target);
      setVal(n);
      if (n >= target) clearInterval(t);
    }, 40);
    return () => clearInterval(t);
  }, [target]);
  return <span>{val.toFixed(2)}</span>;
}
